
import React, { useEffect, useRef, useState } from "react";

import { PART_LABELS } from "./printCardApi";

/*
 * Одна сторона визитки с рамками кусков поверх картинки:
 * щелчок — выбрать кусок, тянуть — сдвинуть, колесо — больше / меньше.
 * Картинку рисует сервер: пока тянем — двигается рамка, отпустили — сервер перерисовывает.
 */

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round = (value, digits = 3) => Math.round(value * 10 ** digits) / 10 ** digits;
const DRAG_THRESHOLD = 3;

const CardCanvas = ({ side, image, layout, parts, selected, onSelect, onPartChange, frames, disabled, loading }) => {
    const overlayRef = useRef(null);
    const [drag, setDrag] = useState(null);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        setPreview(null);
    }, [layout]);

    // Колесо над куском — размер. Обычный слушатель: React вешает колесо «пассивным».
    const wheelRef = useRef({ timer: 0, pending: null });
    wheelRef.current = { ...wheelRef.current, parts, onPartChange, side, disabled };
    useEffect(() => {
        const element = overlayRef.current;
        if (!element) return undefined;
        const onWheel = (event) => {
            const state = wheelRef.current;
            const node = event.target.closest?.("[data-part]");
            if (!node || state.disabled) return;
            event.preventDefault();
            const key = node.getAttribute("data-part");
            const current = state.pending?.key === key ? state.pending.s : state.parts?.[key]?.s ?? 1;
            const next = round(clamp(current + (event.deltaY < 0 ? 0.05 : -0.05), 0.3, 3), 2);
            state.pending = { key, s: next };
            setPreview({ kind: "zoom", key, s: next });
            window.clearTimeout(state.timer);
            state.timer = window.setTimeout(() => {
                state.pending = null;
                state.onPartChange(state.side, key, { s: next });
            }, 250);
        };
        element.addEventListener("wheel", onWheel, { passive: false });
        return () => element.removeEventListener("wheel", onWheel);
    }, [image]);

    if (!image || !layout) return null;

    const pct = (value, total) => `${(value / total) * 100}%`;
    const pxPerMm = () => {
        const rect = overlayRef.current?.getBoundingClientRect();
        return rect ? rect.width / layout.widthMm : 1;
    };

    const start = (event, part) => {
        if (disabled || event.button > 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        onSelect(side, part.key);
        setDrag({ key: part.key, startX: event.clientX, startY: event.clientY, moved: false });
    };

    const move = (event) => {
        if (!drag) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        const moved = drag.moved || Math.hypot(dx, dy) > DRAG_THRESHOLD;
        if (moved !== drag.moved) setDrag({ ...drag, moved });
        if (moved) {
            const k = pxPerMm();
            setPreview({ kind: "move", key: drag.key, dx: dx / k, dy: dy / k });
        }
    };

    const end = () => {
        if (!drag) return;
        if (drag.moved && preview?.kind === "move") {
            const base = parts?.[drag.key] || { x: 0, y: 0 };
            onPartChange(side, drag.key, {
                x: round(clamp((base.x || 0) + preview.dx / layout.trimWidthMm, -1, 1)),
                y: round(clamp((base.y || 0) + preview.dy / layout.trimHeightMm, -1, 1)),
            });
        }
        setDrag(null);
    };

    const info = preview?.kind === "zoom" ? `${PART_LABELS[preview.key]}: ${Math.round(preview.s * 100)}%` : "";
    // Линия реза: всё за ней (вылеты) обрежется.
    const trim = {
        left: pct(layout.bleedMm, layout.widthMm),
        top: pct(layout.bleedMm, layout.heightMm),
        width: pct(layout.trimWidthMm, layout.widthMm),
        height: pct(layout.trimHeightMm, layout.heightMm),
    };

    return (
        <div
            ref={overlayRef}
            className={`signsCard__overlay${drag ? " is-dragging" : ""}${loading ? " is-loading" : ""}`}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
            onPointerDown={(event) => {
                if (event.target === overlayRef.current) onSelect(side, "");
            }}
        >
            {frames && layout.bleedMm ? <div className="signsCard__trim" style={trim} title="Линия реза: за ней — вылеты под обрезку" /> : null}
            {frames ? (layout.parts || []).map((part) => {
                const shift = preview?.kind === "move" && preview.key === part.key ? preview : null;
                const isSelected = selected?.side === side && selected?.key === part.key;
                return (
                    <div
                        key={part.key}
                        data-part={part.key}
                        className={`signsCard__part${isSelected ? " is-selected" : ""}${shift ? " is-moving" : ""}`}
                        style={{
                            left: pct(part.x + (shift?.dx || 0), layout.widthMm),
                            top: pct(part.y + (shift?.dy || 0), layout.heightMm),
                            width: pct(part.w, layout.widthMm),
                            height: pct(part.h, layout.heightMm),
                        }}
                        onPointerDown={(event) => start(event, part)}
                        title={`${PART_LABELS[part.key]}: тяните — сдвиг, колесо — больше / меньше`}
                    >
                        <span>{PART_LABELS[part.key]}</span>
                    </div>
                );
            }) : null}
            {info ? <div className="printSigns__panInfo">{info}</div> : null}
        </div>
    );
};

export default CardCanvas;
